"use client"
import { type ComponentProps, type ReactNode, useCallback } from "react"
import Link from "next/link"

/**
 * A wrapper around the Next.js Link component for internal and external links.
 */

type AnchorProps = Omit<ComponentProps<typeof Link>, "href"> & {
    href: string
    children?: ReactNode
    onClick?: () => void
    /** If true, scrolls to top of page on navigation (default: true for internal links) */
    scrollToTop?: boolean
}

export default function Anchor(props: AnchorProps): ReactNode {
    const { scrollToTop = true, onClick, ...restProps } = props
    
    const handleClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
        onClick?.()
        // Scroll to top on internal navigation
        if (scrollToTop && props.href.startsWith("/")) {
            // Small delay to let navigation complete
            setTimeout(() => {
                window.scrollTo({ top: 0, behavior: "instant" })
            }, 0)
        }
    }, [onClick, scrollToTop, props.href])

    if (props.href.startsWith("/"))
        return (
            <Link
                href={props.href}
                passHref
                onClick={handleClick}
                {...restProps}
            />
        )

    const { prefetch, ...otherProps } = restProps
    return <a href={props.href} onClick={handleClick} {...otherProps} />
}
